import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarClock, Users2, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import RegionFilter from "@/components/RegionFilter";
import { AgendaList, DayGrid, WeekGrid, MonthGrid, TeamGrid } from "@/components/schedule/CalendarViews";
import { EventDetailDialog } from "@/components/schedule/EventDetailDialog";
import { TYPE_STYLES, addDays, rangeFor, shiftedSlot, useClashGuard } from "@/lib/schedule";

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;

const Toggle = ({ value, options, onChange, testPrefix }) => (
  <div className="flex items-center bg-white border border-slate-200 rounded-md p-0.5">
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testPrefix}-${v}`}
        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${value === v ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>{label}</button>
    ))}
  </div>
);

const TeamFilters = ({ meta, engineer, setEngineer, region, setRegion, type, setType }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="team-filters">
    <Select value={engineer || "all"} onValueChange={(v) => setEngineer(v === "all" ? "" : v)}>
      <SelectTrigger className="h-9 w-48 text-xs" data-testid="team-engineer-filter"><SelectValue placeholder="All engineers" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All engineers</SelectItem>
        {(meta?.engineers || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
    <RegionFilter value={region} onChange={setRegion} testId="team-region-filter" />
    <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
      <SelectTrigger className="h-9 w-40 text-xs" data-testid="team-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All event types</SelectItem>
        {(meta?.event_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Legend = () => (
  <div className="flex items-center gap-3 text-[11px] text-slate-500">
    {Object.entries(TYPE_STYLES).map(([t, s]) => <span key={t} className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${s.dot}`} />{t}</span>)}
  </div>
);

export default function Schedule() {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("mine");
  const [view, setView] = useState(() => (isMobile() ? "day" : "week"));
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(null);
  const [engineer, setEngineer] = useState("");
  const [region, setRegion] = useState("");
  const [type, setType] = useState("");
  const guard = useClashGuard();
  const team = tab === "team";
  const effView = team && view === "month" ? "week" : view;
  const [start, end] = useMemo(() => rangeFor(effView, anchor), [effView, anchor]);

  useEffect(() => { api.get("/schedule/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
    if (!team) p.set("mine", "true");
    else { if (engineer) p.set("engineer_id", engineer); if (region) p.set("region_id", region); if (type) p.set("types", type); }
    try { setEvents((await api.get(`/schedule/events?${p}`)).data); } catch (e) { toast.error(formatApiError(e)); }
  }, [start, end, team, engineer, region, type]);
  useEffect(() => { load(); }, [load]);

  const shift = (dir) => setAnchor((a) => (effView === "month" ? new Date(a.getFullYear(), a.getMonth() + dir, 1) : addDays(a, dir * (effView === "week" ? 7 : 1))));

  const onDrop = async (id, day, hour, engineerId) => {
    const ev = events.find((x) => x.id === id);
    if (!ev) return;
    const slot = shiftedSlot(ev, day, hour);
    const moveEngineer = engineerId && engineerId !== ev.engineer_id;
    if (slot.start === new Date(ev.start).toISOString() && !moveEngineer) return;
    const body = moveEngineer ? { ...slot, engineer_id: engineerId } : slot;
    const r = await guard.run((allow) => api.patch(`/schedule/events/${id}/reschedule${allow ? "?allow_clash=true" : ""}`, body));
    if (r) toast.success(moveEngineer ? "Event moved and reassigned" : "Event rescheduled");
    load();
  };

  const days = Array.from({ length: Math.round((end - start) / 86400000) }, (_, i) => addDays(start, i));
  const engineers = (meta?.engineers || []).filter((u) => !engineer || u.id === engineer);
  const title = effView === "month" ? anchor.toLocaleDateString([], { month: "long", year: "numeric" })
    : effView === "week" ? `${start.toLocaleDateString([], { day: "numeric", month: "short" })} – ${addDays(end, -1).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}`
    : anchor.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
  const canDrag = !isMobile();

  return (
    <div data-testid="schedule-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Field service</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-indigo-600" /> {team ? "Team schedule" : "My schedule"}
          </h1>
        </div>
        {meta?.can_assign_others && (
          <Toggle value={tab} onChange={setTab} testPrefix="schedule-tab" options={[["mine", <span key="m" className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />My schedule</span>], ["team", <span key="t" className="inline-flex items-center gap-1"><Users2 className="w-3.5 h-3.5" />Team</span>]]} />
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="schedule-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="schedule-today">Today</Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="schedule-next"><ChevronRight className="w-4 h-4" /></Button>
          <div className="font-heading font-semibold text-slate-900 ml-1 text-sm md:text-base" data-testid="schedule-range-title">{title}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {team && <TeamFilters {...{ meta, engineer, setEngineer, region, setRegion, type, setType }} />}
          <Toggle value={effView} onChange={setView} testPrefix="schedule-view" options={team ? [["day", "Day"], ["week", "Week"]] : [["day", "Day"], ["week", "Week"], ["month", "Month"]]} />
        </div>
      </div>
      <div className="mb-3"><Legend /></div>

      {isMobile() ? (
        team ? engineers.map((u) => (
          <section key={u.id} className="mb-5" data-testid={`team-m-${u.id}`}>
            <div className="font-semibold text-slate-900 mb-2">{u.name}</div>
            <AgendaList events={events.filter((ev) => ev.engineer_id === u.id)} days={days} onOpen={setOpen} />
          </section>
        )) : <AgendaList events={events} days={days} onOpen={setOpen} />
      ) : team ? (
        <TeamGrid events={events} engineers={engineers} days={days} onOpen={setOpen} onDrop={onDrop} canDrag={canDrag} />
      ) : (
        <>
          {events.length === 0 && <div className="mb-3 text-sm text-slate-500" data-testid="schedule-empty">Nothing scheduled in this period.</div>}
          {effView === "day" ? (
            <DayGrid events={events} day={start} onOpen={setOpen} onDrop={onDrop} canDrag={canDrag} />
          ) : effView === "week" ? (
            <WeekGrid events={events} start={start} onOpen={setOpen} onDrop={onDrop} canDrag={canDrag} />
          ) : (
            <MonthGrid events={events} anchor={anchor} start={start} onOpen={setOpen} onDrop={onDrop} canDrag={canDrag} />
          )}
        </>
      )}

      <EventDetailDialog event={open} meta={meta} guard={guard} onClose={() => setOpen(null)} onChanged={() => { setOpen(null); load(); }} />
      {guard.dialog}
    </div>
  );
}
